import { useEffect, useMemo } from "react";
import { ArrowRight, CalendarClock, Clock, ShieldCheck } from "lucide-react";
import { formatGBP } from "@autopic/shared";
import { Button } from "../../components/Button";
import { StepHeader } from "../../components/StepHeader";
import { ServiceIcon } from "../../components/icons";
import { track } from "../../analytics/analytics";
import { useSession } from "../../store/sessionStore";
import { formatLongDate } from "../dates";
import type { StepProps } from "../stepTypes";
import s from "../steps.module.css";

function formatDuration(mins: number): string {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Step — the MOT itself. Confirms the garage's MOT product; services follow as an optional add-on. */
export function MotStep({ onNext, saveLead }: StepProps) {
  const config = useSession((st) => st.config)!;
  const vehicle = useSession((st) => st.vehicle);
  const selectedIds = useSession((st) => st.selectedServiceIds);
  const setServices = useSession((st) => st.setServices);

  const motService = useMemo(
    () => config.services.find((sv) => sv.isMot) ?? null,
    [config.services],
  );

  // Defensive: the step is filtered out for garages without an MOT service,
  // but if we ever land here without one, fall straight through.
  useEffect(() => {
    if (!motService) onNext();
  }, [motService, onNext]);
  if (!motService) return null;

  const motExpiry =
    vehicle && "motExpiryDate" in vehicle ? vehicle.motExpiryDate : undefined;
  const expiryLabel = motExpiry ? formatLongDate(motExpiry.slice(0, 10)) : null;
  const expired = motExpiry ? motExpiry.slice(0, 10) < new Date().toISOString().slice(0, 10) : false;

  function handleContinue() {
    // Lock the MOT into the selection (keep any add-ons picked earlier).
    if (!selectedIds.includes(motService!.id)) {
      setServices([motService!.id, ...selectedIds]);
    }
    track("service_selected", {
      serviceId: motService!.id,
      name: motService!.name,
      price: motService!.price,
      isMot: true,
    });
    saveLead("mot");
    onNext();
  }

  return (
    <div>
      <StepHeader
        kicker="Your MOT"
        title={config.copy.motTitle}
        subtitle="Book the test now — you can add a service on the next step."
        display={false}
      />

      <div className={s.stack}>
        {expiryLabel && (
          <span
            className={s.foundBadge}
            style={
              expired
                ? { background: "rgba(180, 35, 24, 0.08)", color: "var(--ap-danger)" }
                : undefined
            }
          >
            <CalendarClock size={13} />
            {expired ? `MOT expired on ${expiryLabel}` : `MOT due by ${expiryLabel}`}
          </span>
        )}

        <div className={s.vehicleCard}>
          <span className={s.vehicleIcon}>
            <ServiceIcon icon={motService.icon ?? "shield-check"} size={26} />
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p className={s.vehicleName}>{motService.name}</p>
            {motService.description && (
              <p className={s.tileDesc} style={{ marginTop: 4 }}>
                {motService.description}
              </p>
            )}
            <div className={s.vehicleMeta}>
              {motService.durationMins > 0 && (
                <span className={s.metaChip}>
                  <Clock size={11} style={{ verticalAlign: -1, marginRight: 3 }} />
                  approx {formatDuration(motService.durationMins)}
                </span>
              )}
              <span className={s.metaChip}>
                <ShieldCheck size={11} style={{ verticalAlign: -1, marginRight: 3 }} />
                DVSA approved
              </span>
            </div>
          </div>
          <div className={s.tilePrice} style={{ marginTop: 0, textAlign: "right" }}>
            <span className={s.priceNow}>{formatGBP(motService.price)}</span>
            {motService.priceWas != null && motService.priceWas > motService.price && (
              <span className={s.priceWas}>{formatGBP(motService.priceWas)}</span>
            )}
          </div>
        </div>
      </div>

      <div className={s.actions}>
        <Button block onClick={handleContinue} trailingIcon={<ArrowRight size={18} />}>
          {config.copy.ctaContinue}
        </Button>
      </div>
    </div>
  );
}
